import React, { useState, useEffect } from 'react';
import {
  X,
  Clock,
  RefreshCw,
  Save,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  FileText,
  User,
  Phone,
  Target,
  XCircle,
  Info,
  Star
} from 'lucide-react';

const RollOffEditModal = ({ 
  candidate, 
  onSave, 
  onClose, 
  useToast, 
  sendEmail, 
  API_BASE_URL, 
  mode = 'full',
  tabOrder = 'feedback-first'
}) => {
  const { success, error, warning } = useToast();
  
  // const [activeSection, setActiveSection] = useState(1);
  
  const [clientRating, setClientRating] = useState(candidate?.clientRating || 0);
  const [clientFeedback, setClientFeedback] = useState(candidate?.clientFeedback || '');
  const [improvementArea1, setImprovementArea1] = useState(candidate?.improvementArea1 || '');
  const [improvementArea2, setImprovementArea2] = useState(candidate?.improvementArea2 || '');
  const [improvementArea3, setImprovementArea3] = useState(candidate?.improvementArea3 || '');

  const [extensionRequested, setExtensionRequested] = useState(candidate?.extensionRequested ?? null);
  const [pocName, setPocName] = useState(candidate?.pocName || '');
  const [extensionComments, setExtensionComments] = useState(candidate?.extensionComments || '');
  const [noExtensionReason, setNoExtensionReason] = useState(candidate?.noExtensionReason || '');
  const [contactedWith, setContactedWith] = useState(candidate?.contactedWith || '');
  const [extensionDuration, setExtensionDuration] = useState(candidate?.extensionDuration || '');
  const [lastWorkingDate, setLastWorkingDate] = useState(candidate?.lastWorkingDate || '');
  const [lookingForOpportunity, setLookingForOpportunity] = useState(candidate?.lookingForOpportunity || '');

  const [errors, setErrors] = useState({});
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [extensionLoading, setExtensionLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    if (candidate) {
      setContactedWith(candidate.contactedWith || '');
      setExtensionDuration(candidate.extensionDuration || '');
      
      if (candidate.extensionRequested === 1) {
        setExtensionRequested(true);
      } else if (candidate.extensionRequested === 2) {
        setExtensionRequested(false);
      } else {
        setExtensionRequested(null);
      }
      
      if (candidate.extensionStatus && candidate.extensionStatus !== 'Yes' && candidate.extensionStatus !== 'No') {
        setPocName(candidate.extensionStatus);
      }
    }
  }, [candidate]);

  const getModalTitle = () => {
    if (tabOrder === 'feedback-first') {
      return 'Extension Management';
    }
    return 'Roll-off Management & Extension Management';
  };

  const getModalSubtitle = () => {
    if (tabOrder === 'feedback-first') {
      return 'Manage contract extensions';
    }
    return 'Manage contract extensions';
  };

  const validateClientFeedback = () => {
    const newErrors = {};
    
    if (clientRating < 1 || clientRating > 5) {
      newErrors.clientRating = 'Rating must be between 1 and 5';
    }
    
    if (!clientFeedback.trim()) {
      newErrors.clientFeedback = 'Client feedback is required';
    }

    if (!improvementArea1.trim()) {
      newErrors.improvementArea1 = 'First improvement area is required';
    }

    if (!improvementArea2.trim()) {
      newErrors.improvementArea2 = 'Second improvement area is required';
    }

    if (!improvementArea3.trim()) {
      newErrors.improvementArea3 = 'Third improvement area is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateExtensionManagement = () => {
    const newErrors = {};
    
    if (!contactedWith) {
      newErrors.contactedWith = 'Please select who you contacted';
    }
    
    if (contactedWith === 'Contacted – Client/Candidate – Pending Confirmation on Extension') {
      if (!noExtensionReason.trim()) {
        newErrors.pendingReason = 'Reason for pending status is required';
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
    
    if (extensionRequested === null || extensionRequested === undefined) {
      newErrors.extensionChoice = 'Please select whether the contract is being extended or not';
    } else if (extensionRequested === true) {
      if (contactedWith === 'Client') {
        if (!pocName.trim()) {
          newErrors.pocName = 'POC name is required when requesting extension from client';
        }
        if (!extensionDuration) {
          newErrors.extensionDuration = 'Extension duration is required';
        }
      } else if (contactedWith === 'Candidate' || contactedWith === 'Vendor') {
        if (!extensionDuration) {
          newErrors.extensionDuration = 'Extension duration is required';
        }
      }
    } else {
      if (!lastWorkingDate) {
        newErrors.lastWorkingDate = 'Last working date is required';
      }
      if (!noExtensionReason.trim()) {
        newErrors.noExtensionReason = 'Reason for not extending is required';
      } else if (noExtensionReason === 'Others' && !extensionComments.trim()) {
        newErrors.othersComment = 'Please specify the reason when "Others" is selected';
      }
      if (!lookingForOpportunity) {
        newErrors.lookingForOpportunity = 'Please select if candidate is looking for opportunities';
      }
      if (!extensionComments.trim()) {
        newErrors.extensionComments = 'Additional comments are required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExtensionChoice = (choice) => {
    setExtensionRequested(choice);
    
    if (choice === true) {
      setNoExtensionReason('');
      setLastWorkingDate('');
      setLookingForOpportunity('');
    } else if (choice === false) {
      setPocName('');
      setExtensionComments('');
    }
    
    setErrors({});
  };

  const handleFeedbackSave = async () => {
    setFeedbackLoading(true);
    
    try {
      const isValid = validateClientFeedback();
      if (!isValid) {
        console.error('❌ Feedback validation failed:', errors);
        setFeedbackLoading(false);
        return;
      }

      const updateData = {
        id: candidate.id,
        clientRating,
        clientFeedback,
        improvementArea1,
        improvementArea2,
        improvementArea3,
        candidateName: candidate.candidateName || candidate.candidate_name,
        candidateEmail: candidate.candidateEmail || candidate.candidate_email,
        enableFeedbackTracking: true,
        enableExtensionStatusCalc: false,
        enableRDForwarding: false,
        preventExtensionStatusUpdate: true
      };

      console.log('📤 Sending feedback data:', updateData);
      console.log('📤 Feedback data as JSON:', JSON.stringify(updateData, null, 2));

      await onSave(candidate.id, updateData);
      
      candidate.clientRating = clientRating;
      candidate.clientFeedback = clientFeedback;
      candidate.improvementArea1 = improvementArea1;
      candidate.improvementArea2 = improvementArea2;
      candidate.improvementArea3 = improvementArea3;
      
      console.log('✅ Feedback save successful');
      success('Client feedback saved successfully!');
    } catch (err) {
      console.error('❌ FEEDBACK SAVE ERROR:', err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error stack:', err.stack);
      error(`Failed to save: ${err.message}`);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleExtensionSave = async () => {
    setExtensionLoading(true);
    
    try {
      const isValid = validateExtensionManagement();
      if (!isValid) {
        console.error('❌ Extension validation failed:', errors);
        setExtensionLoading(false);
        return;
      }

      let mappedExtensionRequested;
      if (extensionRequested === true || extensionRequested === 1) {
        mappedExtensionRequested = 1;
      } else if (extensionRequested === false || extensionRequested === 2) {
        mappedExtensionRequested = 2;
      } else {
        mappedExtensionRequested = 0;
      }

      const shouldForwardToRD = mappedExtensionRequested === 2 && 
                                lookingForOpportunity === 'Looking for Opportunity';

      const updateData = {
        id: candidate.id,
        contactedWith,
        extensionRequested: mappedExtensionRequested,
        noExtensionReason,
        lastWorkingDate,
        lookingForOpportunity,
        clientRating,
        clientFeedback,
        improvementArea1,
        improvementArea2,
        improvementArea3,
        extensionComments,
        extensionDuration,
        pocName,
        candidateName: candidate.candidateName || candidate.candidate_name,
        candidateEmail: candidate.candidateEmail || candidate.candidate_email,
        candidatePhone: candidate.candidatePhone || candidate.candidate_phone,
        client: candidate.client,
        jobTitle: candidate.jobTitle || candidate.job_title,
        primarySkills: candidate.primarySkills || candidate.primary_skills,
        secondarySkills: candidate.secondarySkills || candidate.secondary_skills,
        endDate: candidate.endDate || candidate.end_date,
        projectCity: candidate.projectCity || candidate.project_city,
        projectState: candidate.projectState || candidate.project_state,
        plcCode: candidate.plcCode || candidate.plc_code,
        sapId: candidate.sapId || candidate.sap_id,
        contractorStatus: candidate.contractorStatus || candidate.contractor_status,
        enableExtensionStatusCalc: true,
        enableFeedbackTracking: false,
        preventExtensionStatusUpdate: false,
        enableRDForwarding: shouldForwardToRD
      };

      console.log('📤 RD Forwarding flag set to:', shouldForwardToRD);
      console.log('📤 Extension requested:', extensionRequested);
      console.log('📤 Looking for opportunity:', lookingForOpportunity);
      console.log('🔍 DEBUG VALUES BEFORE SENDING:');
      console.log('   extensionRequested:', extensionRequested);
      console.log('   lookingForOpportunity:', lookingForOpportunity);
      console.log('   shouldForwardToRD:', shouldForwardToRD);
      console.log('   lookingForOpportunity type:', typeof lookingForOpportunity);
      console.log('   lookingForOpportunity length:', lookingForOpportunity?.length);
      console.log('   enableRDForwarding in payload:', updateData.enableRDForwarding);
      console.log('📤 Sending extension update data:', updateData);
      console.log('📤 Extension data as JSON:', JSON.stringify(updateData, null, 2));

      await onSave(candidate.id, updateData);
      console.log('✅ Extension save successful');

      if (shouldForwardToRD) {
        try {
          console.log('📤 Forwarding candidate to RD table...');
          const rdResponse = await fetch(`${API_BASE_URL}/forward_to_rd.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ candidate_id: candidate.id })
          });
          
          const rdResult = await rdResponse.json();
          
          if (rdResult.success) {
            console.log('✅ Candidate forwarded to RD successfully:', rdResult);
          } else {
            console.error('❌ Failed to forward to RD:', rdResult.error);
          }
        } catch (rdError) {
          console.error('❌ RD forwarding error:', rdError);
        }
      }

      if (contactedWith === 'Contacted – Client/Candidate – Pending Confirmation on Extension') {
        success('Information saved successfully. No email sent as contact is still pending.');
        setTimeout(() => onClose(), 1500);
        return;
      }
      
      setEmailSending(true);
      
      try {
        if (extensionRequested === true) {
          let newEndDate = candidate.endDate || candidate.end_date;
          if (extensionDuration && candidate.endDate) {
            newEndDate = `Current end date (${candidate.endDate}) + ${extensionDuration}`;
          }
          
          const extensionEmailData = {
            pocName: pocName,
            extensionComments,
            extensionDate: candidate.endDate || candidate.end_date,
            extensionStatus: pocName,
            extensionDuration,
            newEndDate: newEndDate,
            clientRating,
            clientFeedback,
            contactedWith,
            candidateName: candidate.candidateName || candidate.candidate_name
          };
          
          console.log('📧 Sending extension email with data:', extensionEmailData);
          await sendEmail('extension_request', candidate.id, extensionEmailData);
          console.log('✅ Extension email sent successfully');
          success('Extension request updated successfully and notification sent to contracts team');
          setTimeout(() => onClose(), 1500);
          
        } else if (extensionRequested === false) {
          const noExtensionEmailData = {
            noExtensionReason,
            lastWorkingDate,
            lookingForOpportunity,
            clientRating,
            clientFeedback,
            sentToRD: true,
            sentToRDDate: new Date().toISOString(),
            contactedWith,
            candidateName: candidate.candidateName || candidate.candidate_name,
            candidateEmail: candidate.candidateEmail || candidate.candidate_email,
            lastWorkingDate: lastWorkingDate,
            extensionComments
          };
          
          console.log('📧 Sending no-extension email with data:', noExtensionEmailData);
          await sendEmail('no_extension', candidate.id, noExtensionEmailData);
          console.log('✅ No-extension email sent successfully');
          success('Extension decision updated successfully and candidate profile sent to RD team for redeployment');
          setTimeout(() => onClose(), 1500);
        }
      } catch (emailError) {
        console.error('❌ EMAIL ERROR:', emailError);
        console.error('❌ Email error message:', emailError.message);
        console.error('❌ Email error stack:', emailError.stack);
        const failureMessage = extensionRequested 
          ? 'Extension decision saved successfully, but failed to notify contracts team. Please contact them manually.'
          : 'Extension decision saved successfully, but failed to notify RD team. Please contact them manually.';
        warning(failureMessage);
      } finally {
        setEmailSending(false);
      }
    } catch (err) {
      console.error('❌ COMPLETE EXTENSION ERROR OBJECT:', err);
      console.error('❌ Error message:', err.message);
      console.error('❌ Error stack:', err.stack);
      console.error('❌ Error response:', err.response);
      
      if (err.response) {
        console.error('❌ Response status:', err.response.status);
        console.error('❌ Response data:', err.response.data);
      }
      
      error(`Failed to save: ${err.message}`);
    } finally {
      setExtensionLoading(false);
    }
  };

  if (!candidate) return null;

  const renderClientFeedback = () => (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-2xl border border-red-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-pink-600 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Client Feedback Collection</h3>
        </div>
        <p className="text-slate-600 text-sm">Collect and document client performance feedback</p>
        
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <Info className="w-3 h-3 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Instructions</p>
              <p className="text-sm text-blue-800">
                This section should be completed by asking the client about the candidate's overall performance. 
                Suggested question: "On a scale of 1 to 5, how would you rate the overall performance of the candidate?"
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <CheckCircle className="w-4 h-4 mr-2 text-red-600" />
          Client Rating (1-5 stars) *
        </label>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setClientRating(star)}
                disabled={feedbackLoading || extensionLoading}
                className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                  star <= clientRating 
                    ? 'text-yellow-400 bg-yellow-50 hover:bg-yellow-100' 
                    : 'text-slate-300 hover:text-yellow-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Star className={`w-7 h-7 ${star <= clientRating ? 'fill-yellow-400' : ''}`} />
              </button>
            ))}
            <div className="ml-4 px-4 py-2 bg-slate-100 rounded-lg border border-slate-200">
              <span className="text-sm font-medium text-slate-700">
                {clientRating > 0 ? (
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span>{clientRating}/5 Stars</span>
                    <span className="text-xs text-slate-500">
                      ({clientRating >= 4 ? 'Excellent' : clientRating >= 3 ? 'Good' : clientRating >= 2 ? 'Fair' : 'Poor'})
                    </span>
                  </div>
                ) : (
                  'No rating selected'
                )}
              </span>
            </div>
          </div>
        </div>
        {errors.clientRating && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.clientRating}</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <Target className="w-4 h-4 mr-2 text-red-600" />
          3 Key Areas for Improvement *
        </label>
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-3">
          <div className="flex items-start space-x-2">
            <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <Info className="w-2.5 h-2.5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs font-medium text-blue-900 mb-1">Instructions</p>
              <p className="text-xs text-blue-800">
                Please identify the top 3 areas where the candidate can improve their performance.
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          {[
            { value: improvementArea1, setter: setImprovementArea1, error: 'improvementArea1', placeholder: 'e.g., Communication with team members' },
            { value: improvementArea2, setter: setImprovementArea2, error: 'improvementArea2', placeholder: 'e.g., Time management and meeting deadlines' },
            { value: improvementArea3, setter: setImprovementArea3, error: 'improvementArea3', placeholder: 'e.g., Technical documentation skills' }
          ].map((area, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-red-600">{index + 1}</span>
              </div>
              <input
                type="text"
                value={area.value}
                onChange={(e) => area.setter(e.target.value)}
                disabled={feedbackLoading || extensionLoading}
                className={`flex-1 px-4 py-2 border-2 rounded-lg focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
                  errors[area.error] ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                }`}
                placeholder={area.placeholder}
              />
            </div>
          ))}
        </div>
        
        {(errors.improvementArea1 || errors.improvementArea2 || errors.improvementArea3) && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Please fill in all 3 areas for improvement</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <FileText className="w-4 h-4 mr-2 text-red-600" />
          Client Feedback Comments *
        </label>
        <textarea
          value={clientFeedback}
          onChange={(e) => setClientFeedback(e.target.value)}
          disabled={feedbackLoading || extensionLoading}
          rows={4}
          className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
            errors.clientFeedback ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
          }`}
          placeholder="Enter detailed client feedback about the candidate's performance, strengths, and overall satisfaction..."
        />
        {errors.clientFeedback && (
          <div className="flex items-center space-x-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.clientFeedback}</span>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-6 border-t border-slate-200">
        <button
          onClick={handleFeedbackSave}
          disabled={feedbackLoading || extensionLoading}
          className="px-8 py-3 bg-gradient-to-r from-red-600 to-pink-600 text-white rounded-xl hover:from-red-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {feedbackLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Saving Feedback...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Save Client Feedback</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderExtensionManagement = () => (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-2xl border border-orange-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-orange-600 to-red-600 rounded-xl flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Extension Management</h3>
        </div>
        <p className="text-slate-600 text-sm">Manage contract extension requests and decisions</p>
        
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <Info className="w-3 h-3 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Instructions</p>
              <p className="text-sm text-blue-800">
                This section should be filled when contacting the client to check on the status of the project extension.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
        <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
          <Phone className="w-5 h-5 mr-2 text-blue-600" />
          Contact Information
        </h4>
        <div className="space-y-3">
          <label className="text-sm font-semibold text-slate-700">Contacted with: *</label>
          <select
            value={contactedWith}
            onChange={(e) => {
              setContactedWith(e.target.value);
              setExtensionRequested(null);
              setPocName('');
              setExtensionDuration('');
              setExtensionComments('');
              setNoExtensionReason('');
              setLastWorkingDate('');
              setLookingForOpportunity('');
              setErrors({});
            }}
            disabled={feedbackLoading || extensionLoading || emailSending}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 disabled:opacity-50 ${
              errors.contactedWith ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
          >
            <option value="">Select who you contacted...</option>
            <option value="Client">Client</option>
            <option value="Candidate">Candidate</option>
            <option value="Vendor">Vendor</option>
            <option value="Contacted – Client/Candidate – Pending Confirmation on Extension">Contacted – Client/Candidate – Pending Confirmation on Extension</option>
          </select>
          {errors.contactedWith && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.contactedWith}</span>
            </div>
          )}
        </div>
      </div>

      {contactedWith && contactedWith !== 'Contacted – Client/Candidate – Pending Confirmation on Extension' && (
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-6 rounded-2xl border border-slate-200">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <Target className="w-5 h-5 mr-2 text-blue-600" />
            Candidate Contact Status
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <input
                type="radio"
                id="extensionYes"
                name="extensionRequest"
                checked={extensionRequested === true}
                disabled={feedbackLoading || extensionLoading || emailSending}
                onChange={() => handleExtensionChoice(true)}
                className="sr-only"
              />
              <label 
                htmlFor="extensionYes" 
                className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                  extensionRequested === true 
                    ? 'border-green-500 bg-green-50 text-green-900' 
                    : 'border-slate-300 hover:border-slate-400 bg-white'
                } ${(feedbackLoading || extensionLoading || emailSending) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                  extensionRequested === true ? 'border-green-500 bg-green-500' : 'border-slate-300'
                }`}>
                  {extensionRequested === true && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
                <div>
                  <span className="font-semibold">Yes - Contract Extended</span>
                  <p className="text-sm text-slate-600 mt-1">Client has agreed to extend the contract</p>
                </div>
              </label>
            </div>
            
            <div className="relative">
              <input
                type="radio"
                id="extensionNo"
                name="extensionRequest"
                checked={extensionRequested === false}
                disabled={feedbackLoading || extensionLoading || emailSending}
                onChange={() => handleExtensionChoice(false)}
                className="sr-only"
              />
              <label 
                htmlFor="extensionNo" 
                className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                  extensionRequested === false 
                    ? 'border-red-500 bg-red-50 text-red-900' 
                    : 'border-slate-300 hover:border-slate-400 bg-white'
                } ${(feedbackLoading || extensionLoading || emailSending) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className={`w-5 h-5 rounded-full border-2 mr-3 flex items-center justify-center ${
                  extensionRequested === false ? 'border-red-500 bg-red-500' : 'border-slate-300'
                }`}>
                  {extensionRequested === false && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
                <div>
                  <span className="font-semibold">No - Contract Ending</span>
                  <p className="text-sm text-slate-600 mt-1">Contract will end as scheduled</p>
                </div>
              </label>
            </div>
          </div>
          
          {extensionRequested !== null && (
            <div className="mt-4 p-3 bg-white border border-slate-300 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                  extensionRequested ? 'bg-green-500' : 'bg-red-500'
                }`}>
                  {extensionRequested ? (
                    <CheckCircle className="w-4 h-4 text-white" />
                  ) : (
                    <XCircle className="w-4 h-4 text-white" />
                  )}
                </div>
                <span className="text-sm font-semibold text-slate-700">
                  Selected: {extensionRequested ? 'YES - Contract Extended' : 'NO - Contract Ending'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {extensionRequested && contactedWith && contactedWith !== 'Contacted – Client/Candidate – Pending Confirmation on Extension' && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-200">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
            Contract Extension Details
          </h4>
          
          <div className="space-y-6">
            {contactedWith === 'Client' && (
              <div className="space-y-3">
                <label className="flex items-center text-sm font-semibold text-slate-700">
                  <User className="w-4 h-4 mr-2 text-green-600" />
                  POC (Client Contact Name) *
                </label>
                <input
                  type="text"
                  value={pocName}
                  onChange={(e) => setPocName(e.target.value)}
                  disabled={feedbackLoading || extensionLoading || emailSending}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 disabled:opacity-50 ${
                    errors.pocName ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                  }`}
                  placeholder="Enter the name of the client contact who confirmed the extension"
                />
                {errors.pocName && (
                  <div className="flex items-center space-x-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.pocName}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              <label className="flex items-center text-sm font-semibold text-slate-700">
                <Clock className="w-4 h-4 mr-2 text-green-600" />
                Extension Duration *
              </label>
              <select
                value={extensionDuration}
                onChange={(e) => setExtensionDuration(e.target.value)}
                disabled={feedbackLoading || extensionLoading || emailSending}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 disabled:opacity-50 ${
                  errors.extensionDuration ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <option value="">Select extension duration...</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
                <option value="4 months">4 months</option>
                <option value="5 months">5 months</option>
                <option value="6 months">6 months</option>
                <option value="9 months">9 months</option>
                <option value="12 months">12 months</option>
                <option value="Other">Other</option>
              </select>
              {errors.extensionDuration && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.extensionDuration}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <label className="flex items-center text-sm font-semibold text-slate-700">
                <FileText className="w-4 h-4 mr-2 text-green-600" />
                Additional Comments
              </label>
              <textarea
                value={extensionComments}
                onChange={(e) => setExtensionComments(e.target.value)}
                disabled={feedbackLoading || extensionLoading || emailSending}
                rows={4}
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-green-500/20 focus:border-green-500 hover:border-slate-400 transition-all duration-200 disabled:opacity-50"
                placeholder="Additional details about the extension request..."
              />
            </div>
          </div>
        </div>
      )}

      {extensionRequested === false && contactedWith && contactedWith !== 'Contacted – Client/Candidate – Pending Confirmation on Extension' && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-2xl border border-red-200">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <AlertCircle className="w-5 h-5 mr-2 text-red-600" />
            Contract Ending Details
          </h4>
          <div className="space-y-6">
            <div className="space-y-3">
              <label className="flex items-center text-sm font-semibold text-slate-700">
                <Clock className="w-4 h-4 mr-2 text-red-600" />
                Last Working Date *
              </label>
              <input
                type="date"
                value={lastWorkingDate}
                onChange={(e) => setLastWorkingDate(e.target.value)}
                disabled={feedbackLoading || extensionLoading || emailSending}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
                  errors.lastWorkingDate ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              />
              {errors.lastWorkingDate && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.lastWorkingDate}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <label className="flex items-center text-sm font-semibold text-slate-700">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Reason for Contract Ending *
              </label>
              <select
                value={noExtensionReason}
                onChange={(e) => setNoExtensionReason(e.target.value)}
                disabled={feedbackLoading || extensionLoading || emailSending}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
                  errors.noExtensionReason ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <option value="">Select a reason...</option>
                <option value="Resignation">Resignation</option>
                <option value="Project Completion">Project Completion</option>
                <option value="FTE Conversion by Client">FTE Conversion by Client</option>
                <option value="Termination">Termination</option>
                <option value="Work Authorization Expiry">Work Authorization Expiry</option>
                <option value="Budget Constraints">Budget Constraints</option>
                <option value="Others">Others</option>
              </select>
              {errors.noExtensionReason && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.noExtensionReason}</span>
                </div>
              )}
            </div>

            {noExtensionReason === 'Others' && (
              <div className="space-y-3">
                <label className="flex items-center text-sm font-semibold text-slate-700">
                  <MessageSquare className="w-4 h-4 mr-2 text-red-600" />
                  Please specify the reason *
                </label>
                <textarea
                  value={extensionComments}
                  onChange={(e) => setExtensionComments(e.target.value)}
                  disabled={feedbackLoading || extensionLoading || emailSending}
                  rows={3}
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
                    errors.othersComment ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                  }`}
                  placeholder="Please provide details..."
                />
                {errors.othersComment && (
                  <div className="flex items-center space-x-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.othersComment}</span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-3">
              <label className="flex items-center text-sm font-semibold text-slate-700">
                <Target className="w-4 h-4 mr-2 text-red-600" />
                Is the candidate looking for new opportunities? *
              </label>
              <select
                value={lookingForOpportunity}
                onChange={(e) => setLookingForOpportunity(e.target.value)}
                disabled={feedbackLoading || extensionLoading || emailSending}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
                  errors.lookingForOpportunity ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                }`}
              >
                <option value="">Select an option...</option>
                <option value="Looking for Opportunity">Looking for Opportunity</option>
                <option value="Not Looking for Opportunity">Not Looking for Opportunity</option>
              </select>
              {errors.lookingForOpportunity && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.lookingForOpportunity}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <label className="flex items-center text-sm font-semibold text-slate-700">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Additional Comments *
              </label>
              <textarea
                value={noExtensionReason === 'Others' ? '' : extensionComments}
                onChange={(e) => {
                  if (noExtensionReason !== 'Others') {
                    setExtensionComments(e.target.value);
                  }
                }}
                disabled={feedbackLoading || extensionLoading || emailSending || noExtensionReason === 'Others'}
                rows={4}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 disabled:opacity-50 ${
                  errors.extensionComments ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
                }`}
                placeholder="Provide any additional information about the contract ending..."
              />
              {errors.extensionComments && (
                <div className="flex items-center space-x-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.extensionComments}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {contactedWith === 'Contacted – Client/Candidate – Pending Confirmation on Extension' && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl border border-yellow-200">
          <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-yellow-600" />
            Pending Information
          </h4>
          <div className="space-y-3">
            <label className="flex items-center text-sm font-semibold text-slate-700">
              <FileText className="w-4 h-4 mr-2 text-yellow-600" />
              Reason for Pending Status *  
            </label>
            <textarea
              value={noExtensionReason}
              onChange={(e) => setNoExtensionReason(e.target.value)}
              disabled={feedbackLoading || extensionLoading}
              rows={3}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-4 focus:ring-yellow-500/20 focus:border-yellow-500 hover:border-slate-400 transition-all duration-200 disabled:opacity-50"
              placeholder="Explain why information is still pending..."
            />
            {errors.pendingReason && (
              <div className="flex items-center space-x-2 text-red-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.pendingReason}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {emailSending && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-900">Sending Email Notification...</p>
              <p className="text-xs text-blue-700">
                {extensionRequested 
                  ? 'Notifying contracts team about extension request' 
                  : 'Sending candidate profile to RD team for redeployment'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-6 border-t border-slate-200">
        <button
          onClick={handleExtensionSave}
          disabled={feedbackLoading || extensionLoading || emailSending}
          className={`px-8 py-3 rounded-xl transition-all duration-200 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2 ${
            contactedWith === 'Contacted – Client/Candidate – Pending Confirmation on Extension'
              ? 'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white'
              : extensionRequested 
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
                : extensionRequested === false
                  ? 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white'
                  : 'bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white'
          }`}
        >
          {extensionLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Saving Decision...</span>
            </>
          ) : emailSending ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Sending Email...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>
                {contactedWith === 'Contacted – Client/Candidate – Pending Confirmation on Extension'
                  ? 'Save Pending Status'
                  : extensionRequested 
                    ? 'Save & Notify Contracts Team' 
                    : extensionRequested === false 
                      ? 'Save & Send to RD Team' 
                      : 'Save Extension Decision'
                }
              </span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden border border-slate-200">
        <div className="bg-gradient-to-r from-red-600 via-pink-600 to-purple-600 px-8 py-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-purple-500/20"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{getModalTitle()}</h2>
                <p className="text-red-100 text-sm">{getModalSubtitle()} • {candidate.candidateName}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              disabled={feedbackLoading || extensionLoading || emailSending}
              className="p-3 hover:bg-white/20 rounded-xl transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        <div className="p-8 max-h-[calc(95vh-200px)] overflow-y-auto">
  {renderExtensionManagement()}
</div>
      </div>
    </div>
  );
};

export default RollOffEditModal;